// ─────────────────────────────────────────────────────────────────────────────
// 🔑 DOCUMENTS, FOR A BORROWER ON THE GUIDED FLOW (Jesse, 2026-10-16: the Verification page is retired per
// borrower and "Documents becomes the home for the application").
//
//   · Action needed (#action-needed) — every open need the borrower can see (GET /api/guided-flow/needs), each
//     with the step that answers it. 🚨 NO UPLOADS FOR NEEDS ON THIS PAGE (Jesse, 2026-10-28): uploading is step 5's —
//     a need points there ("Go to step N"; an owed step-5 need holds step 5 open, constants/guidedStepGate, so the
//     shell lands on it). Open conditions are listed with "Go to step 7" — answered on step 7, beside the letters
//     (components/guided/Step7DocumentConditions). 🚨 A staff-flagged section is a step-5 request now (backend
//     utils/sectionFlagNeed — listed here as a need, answered on its card). Each "Go to step N" is the alerts' deep link
//     (/apply/guided?step=N#<row>): a step the borrower has passed opens in view mode, never moving them.
//   · Identity — "Identity verified" and the date. 🚨 Never the ID images (the server does not send them).
//   · Application documents / Disclosures & notices / Other (GET /api/documents/guided-view): every upload,
//     viewable; a replaced version stays, marked Replaced; duplicates labelled.
//     🚨 VIEW-ONLY (Jesse, 2026-10-28): no per-row menu — uploading a newer version, "This isn't mine" and removing
//     happen on step 5. No delete — borrowers cannot delete (the server answers 403).
//     🔑 View opens the real file at a signed link in the app's viewer (utils/openDocument) — never a blob.
//   · The line that an alert will bring a link. 🚨 NO "ADD ASSETS" AFTER THE DECISION (Jesse, 2026-11-02): the
//     post-decision re-price is removed; after a decision, changes go through "Request a change" (EditApplicationCard).
// 🚨 VIEW-ONLY, ALL OF IT (Jesse, 2026-10-28): no file input anywhere on this page (backend test/guidedStepView scans it).
//   · The Edit Application card above Action needed, when that component exists.
// A classic borrower — and everyone under GUIDED_FLOW_ROLLBACK=1 — never renders this (pages/Documents.js).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import LoanDisclosuresCard, { LE_CD_READY_STATUSES } from './LoanDisclosuresCard';
import { RequestsWeSent } from './GuidedDocumentsActions';
import { guidedColumn } from '../constants/guidedLayout';
import { layoutA, readableName } from '../constants/guidedDocumentsLayout';
import { openDocument, prepareDocument } from '../utils/openDocument';
import { DOCUMENTS_SEEN_EVENT } from '../constants/navBadges';

// 🔑 THE IDENTITY CARD'S WORDS, GUIDED BORROWERS (Jesse, 2026-10-19). The state is the server's
// (backend/constants/guidedIdentityCard). No "our team is reviewing it" — nothing here implies a person reviews the ID,
// and nothing hints at a flag or a hold beyond the hold's own sentence.
export const IDENTITY_CARD_COPY = Object.freeze({
  not_started: 'Verify your identity to continue your application.',
  saved: 'Your ID photos are saved. Submit them to finish this step.',
  checking: 'Checking your ID.',
  needs_fix: 'We need a clearer photo of your ID.',
  hold: 'We\u2019re finishing a check on your ID and will let you know when you can continue.',
  received: 'Received.',
});

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 18px', marginBottom: 16 };
const btn = { fontSize: 12.5, fontWeight: 700, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 };

// inShell (Jesse, 2026-10-19): rendered inside the guided shell, whose header already offers "Back to my application".
export default function GuidedDocuments({ token, inShell = false }) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const openedFor = useRef(null);
  const [view, setView] = useState(null);
  const [needs, setNeeds] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [bump, setBump] = useState(0);
  const [dmDocs, setDmDocs] = useState([]);

  const load = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    const [v, n, dm] = await Promise.all([
      axios.get(`${API}/api/documents/guided-view`, { headers: h }).then((r) => r.data).catch(() => ({ groups: [], identity: {}, failed: true })),
      axios.get(`${API}/api/guided-flow/needs`, { headers: h }).then((r) => r.data.needs || []).catch(() => []),
      // 🔑 The LE / CD records the classic card reads, so their signing status shows here too.
      axios.get(`${API}/api/disclosures/borrower-docs`, { headers: h }).then((r) => r.data.docs || []).catch(() => []),
    ]);
    setView(v); setNeeds(n); setDmDocs(dm); setBump((b) => b + 1);
    // 🔑 THE DOCUMENTS BADGE CLEARS ON SIGHT (item 6): the server stamped this visit (GET /guided-view); the nav drops
    // its count now rather than at the next page.
    if (!v.failed) { try { window.dispatchEvent(new Event(DOCUMENTS_SEEN_EVENT)); } catch (_) { /* no window */ } }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // 🔑 The anchor an old /verification link was sent to (#action-needed) scrolls into view once loaded.
  // 🔑 THE ALERTS' "View notice" (Jesse, 2026-10-28 — backend constants/alertDisplay): #notice-<documentId> scrolls to
  // that row of "We sent you" and opens the notice (the page's own open-a-document path), once per link;
  // #documents-we-sent scrolls to the section.
  useEffect(() => {
    const h = hash || window.location.hash;
    if (!view || !h) return;
    const el = document.getElementById(h.slice(1));
    if (el) el.scrollIntoView({ block: 'start' });
    const m = h.match(/^#notice-(\d+)$/);
    if (m && openedFor.current !== h) {
      openedFor.current = h;
      const doc = (view.groups || []).flatMap((g) => g.documents).find((d) => String(d.id) === m[1]);
      if (doc && doc.viewable) openView(doc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, hash]);

  // 🔑 The real file at a signed link, in the app's viewer (utils/openDocument); "first viewed" is stamped by the
  // server when it serves the bytes, not here.
  const openView = async (doc) => {
    setBusy(`view-${doc.id}`); setError(null);
    try { await openDocument({ id: doc.id, name: doc.display_name || readableName(doc) }, token, navigate); }
    catch (_) { setError('That document could not be opened. Please try again.'); }
    finally { setBusy(null); }
  };

  if (!view || !needs) return <div style={{ padding: 24, color: '#6b7280', fontSize: 14 }}>Loading your documents…</div>;
  const actionCount = needs.length + (view.conditions || []).length;
  // 🔑 THE GUIDED DOCUMENTS PAGE — Hannah's pick, variant A (Jesse, 2026-10-20): in the shell, and at /documents.
  return (
    <DocumentsPage inShell={inShell} view={view} needs={needs} dmDocs={dmDocs} actionCount={actionCount} token={token} busy={busy}
      error={error} bump={bump} load={load} openView={openView} navigate={navigate} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE GUIDED DOCUMENTS PAGE (Jesse, 2026-10-19; Hannah picked variant A, 2026-10-20) — constants/guidedDocumentsLayout.
// "Needed from you" only when something is, at the top; "You sent us" (Identity, Income, Assets, Other) and "We sent
// you" (Disclosures & notices, Signed documents), newest first, empty groups hidden; one row per document (readable
// name · date · View). 🚨 No ⋯ menu (Jesse, 2026-10-28): Documents is view-only; a newer version, "This isn't mine"
// and removing are step 5's. No "Edit your application", no filler, nothing that says a person reviews the ID.
// ─────────────────────────────────────────────────────────────────────────────
const H2 = { fontSize: 17, fontWeight: 800, color: '#111827', margin: '4px 0 10px' };
const SUB = { fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6b7280', margin: '14px 0 2px' };
const pill = (bg, fg) => ({ fontSize: 11, fontWeight: 700, background: bg, color: fg, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' });

function DocRow({ d, openView, prepareView, busy, notice = false }) {
  return (
    // 🔑 id: a "We sent you" row is notice-<documentId> (the alerts' "View notice" anchor).
    <div data-testid="documents-row" id={notice ? `notice-${d.id}` : undefined} style={{ borderTop: '1px solid #f3f4f6', padding: '9px 0', fontSize: 13.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span data-testid="documents-row-name" style={{ fontWeight: 600, color: d.replaced ? '#9ca3af' : '#111827', minWidth: 0, flex: '1 1 200px' }}>{d.display_name || readableName(d)}</span>
        {d.replaced ? <span data-testid="documents-replaced" style={pill('#f3f4f6', '#6b7280')}>Replaced</span> : null}
        {d.not_mine ? <span style={pill('#fffbeb', '#92400e')}>You told us this isn&rsquo;t yours</span> : null}
        <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(d.uploaded_at)}</span>
        {d.viewable ? (
          <button type="button" data-testid="documents-row-view" style={btn} disabled={busy === `view-${d.id}`}
            // 🔑 The link is minted on the way to the tap, so the click can open the real PDF tab (utils/openDocument).
            onPointerEnter={() => prepareView(d)} onFocus={() => prepareView(d)} onPointerDown={() => prepareView(d)} onTouchStart={() => prepareView(d)}
            onClick={() => openView(d)}>
            {busy === `view-${d.id}` ? 'Opening…' : 'View'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function IdentityRows({ identity, navigate }) {
  const st = identity && identity.state;
  if (!st) return null;
  return (
    <div data-testid="documents-identity" data-state={st}>
      <div data-testid="documents-identity-status" style={{ fontSize: 13.5, color: st === 'verified' ? '#166534' : '#374151', padding: '6px 0' }}>
        {st === 'verified' ? `Verified${identity.verified_at ? ` · ${fmtDate(identity.verified_at)}` : ''}` : IDENTITY_CARD_COPY[st] || IDENTITY_CARD_COPY.not_started}
        {['not_started', 'saved', 'needs_fix'].includes(st) ? (
          <button type="button" data-testid="documents-identity-step4" style={{ ...btn, marginLeft: 10 }} onClick={() => navigate('/apply/guided')}>Go to step 4</button>
        ) : null}
      </div>
      {(identity.sides || []).map((sd) => (
        <div key={sd.side} data-testid={`documents-identity-side-${sd.side}`} style={{ borderTop: '1px solid #f3f4f6', padding: '9px 0', fontSize: 13.5, display: 'flex', gap: 10 }}>
          <span style={{ fontWeight: 600, flex: '1 1 200px' }}>{sd.label}</span>
          <span style={{ color: '#6b7280' }}>{sd.uploaded_at ? fmtDate(sd.uploaded_at) : ''}</span>
        </div>
      ))}
    </div>
  );
}

function DocumentsPage({ inShell, view, needs, dmDocs, actionCount, token, busy, error, bump, load, openView, navigate }) {
  const docs = (view.groups || []).flatMap((g) => g.documents);
  const [you, we] = layoutA(docs);
  const hasIdentity = !!(view.identity && view.identity.state);
  const rowProps = { openView, prepareView: (d) => prepareDocument(d.id, token), busy };
  // An LE / CD waiting for the borrower's signature — the Loan Disclosures card's own rule ("Pending Your Signature").
  const itpSigned = docs.some((d) => d.document_type === 'itp_signed_le');
  const pendingSign = (dmDocs || []).some((d) => d && ['le', 'cd'].includes(d.doc_type) && !d.all_signed_at
    && LE_CD_READY_STATUSES.includes(d.package_status) && !(d.doc_type === 'le' && itpSigned));
  const showAction = actionCount > 0 || pendingSign;
  // 🔑 THE ALERTS' DEEP LINK (Jesse, 2026-10-28): the step and the row; a passed step opens in view mode (never a move).
  const goTo = (route) => navigate(route, inShell ? { state: { from: '/apply/guided/documents' } } : undefined);
  return (
    // In the shell: the shell's column and gutter (it draws the back link). At /documents: the app's page, with its own.
    // 🔑 HANNAH'S REVIEW (Jesse, 2026-11-02, items 1 and 3): at /documents (opened from the dashboard or the nav) there
    // is no "Back to my application" — that link is the guided shell's, inside the steps — and the page is as wide as
    // the dashboard's content (the app shell's main column), not a 760px card.
    <div data-testid="guided-documents" data-in-shell={inShell ? 'true' : 'false'}
      style={inShell ? { ...guidedColumn, padding: '12px 0 32px' } : { width: '100%', padding: '0 0 48px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 14px' }}>Documents</h1>
      {error ? <div role="alert" style={{ ...card, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', fontSize: 13 }}>{error}</div> : null}

      {/* The action block — only when something is needed, with a link to where. */}
      {showAction && (
        <section id="action-needed" data-testid="documents-action-needed" style={{ ...card, background: '#fffbeb', borderColor: '#fde68a' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e' }}>Needed from you{actionCount ? ` (${actionCount})` : ''}</div>
          {/* 🔑 A condition is answered on step 7, beside the letters (Jesse, 2026-10-28) — listed here, answered there. */}
          {(view.conditions || []).map((c) => (
            <div key={`c-${c.id}`} data-testid="documents-condition" style={{ borderTop: '1px solid #fde68a', padding: '10px 0', fontSize: 13, color: '#78350f' }}>
              <div style={{ fontWeight: 700 }}>{c.condition_text}</div>
              <div style={{ marginTop: 6 }}>
                <button type="button" data-testid="documents-condition-go" style={btn} onClick={() => goTo(`/apply/guided?step=7#file-condition-${c.id}`)}>Go to step 7</button>
              </div>
            </div>
          ))}
          {needs.map((n) => (
            <div key={n.id} data-testid="documents-need" style={{ borderTop: '1px solid #fde68a', padding: '10px 0', fontSize: 13, color: '#78350f' }}>
              <div style={{ fontWeight: 700 }}>{n.title || 'A document we still need'}</div>
              {n.description ? <div style={{ marginTop: 2, lineHeight: 1.45 }}>{n.description}</div> : null}
              <div style={{ marginTop: 6 }}>
                {/* 🚨 No upload here: uploading is step 5's (Jesse, 2026-10-28). */}
                <button type="button" data-testid="documents-need-go" style={btn} onClick={() => goTo(`/apply/guided?step=${n.step || 5}#need-card-${n.id}`)}>Go to step {n.step || 5}</button>
              </div>
            </div>
          ))}
          {pendingSign ? <LoanDisclosuresCard token={token} dmDocs={dmDocs} docs={docs.map((d) => ({ ...d, upload_date: d.uploaded_at }))} /> : null}
        </section>
      )}

      {hasIdentity || you.sections.length > 0 ? (
        <section data-testid="documents-you-sent" style={card}>
          <div style={H2}>You sent us</div>
          {hasIdentity ? (<><div style={SUB}>Identity</div><IdentityRows identity={view.identity} navigate={navigate} /></>) : null}
          {you.sections.map((sec) => (
            <div key={sec.key} data-testid={`documents-section-${sec.key}`}>
              <div style={SUB}>{sec.title}</div>
              {sec.items.map((d) => <DocRow key={d.id} d={d} {...rowProps} />)}
            </div>
          ))}
        </section>
      ) : null}
      {we.sections.length > 0 ? (
        <section data-testid="documents-we-sent" id="documents-we-sent" style={card}>
          <div style={H2}>We sent you</div>
          {we.sections.map((sec) => (
            <div key={sec.key} data-testid={`documents-section-${sec.key}`}>
              <div style={SUB}>{sec.title}</div>
              {sec.items.map((d) => <DocRow key={d.id} d={d} {...rowProps} notice />)}
            </div>
          ))}
        </section>
      ) : null}

      <RequestsWeSent token={token} netSheet={(view.requests || {}).net_sheet} rentFree={(view.requests || {}).rent_free} onChanged={load} />

      <section data-testid="documents-add" style={card}>
        {/* 🚨 No upload here: uploading is step 5's (Jesse, 2026-10-28). */}
        {/* 🔑 Jesse, 2026-10-28: no link to a step — a borrower past step 5 would land nowhere to add it. */}
        {/* 🚨 No "Add assets" after the decision (Jesse, 2026-11-02): "Request a change" is the only path. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div data-testid="documents-add-pointer" style={{ fontSize: 13.5, color: '#374151', flex: '1 1 240px' }}>When we need a document, we&rsquo;ll send you an alert with a link to add it.</div>
        </div>
      </section>
    </div>
  );
}
